import React, { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Camera, Play, Pause, RotateCcw, Volume2, VolumeX, Maximize } from 'lucide-react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Text, Box } from '@react-three/drei';
import GlassCard from '../components/UI/GlassCard';
import FloatingElement from '../components/UI/FloatingElement';

const ARCookingAssistant: React.FC = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const cookingSteps = [
    {
      id: 1,
      title: "Prepare Ingredients",
      description: "Wash and chop all vegetables. Measure out spices and seasonings.",
      duration: "3 minutes",
      technique: "knife_skills",
      voiceInstruction: "Start by washing all your vegetables thoroughly. Then, using proper knife technique, chop them into uniform pieces."
    },
    {
      id: 2,
      title: "Heat the Pan",
      description: "Heat olive oil in a large skillet over medium-high heat.",
      duration: "2 minutes",
      technique: "heat_control",
      voiceInstruction: "Heat your pan over medium-high heat. Add a tablespoon of olive oil and wait for it to shimmer."
    },
    {
      id: 3,
      title: "Sauté Aromatics",
      description: "Add onions and garlic, cook until fragrant and translucent.",
      duration: "4 minutes",
      technique: "sauteing",
      voiceInstruction: "Add the chopped onions first, then the garlic. Stir frequently to prevent burning."
    },
    {
      id: 4,
      title: "Add Main Ingredients",
      description: "Add the main protein and vegetables, season with salt and pepper.",
      duration: "8 minutes",
      technique: "cooking_protein",
      voiceInstruction: "Now add your protein and vegetables. Season with salt and pepper to taste."
    },
    {
      id: 5,
      title: "Final Seasoning",
      description: "Taste and adjust seasoning. Add fresh herbs if desired.",
      duration: "2 minutes",
      technique: "seasoning",
      voiceInstruction: "Taste your dish and adjust the seasoning as needed. Fresh herbs can be added now."
    }
  ];

  const techniques3D = {
    knife_skills: { position: [-2, 0, 0] as const, color: "#ff6b6b" },
    heat_control: { position: [0, 0, 0] as const, color: "#4ecdc4" },
    sauteing: { position: [2, 0, 0] as const, color: "#45b7d1" },
    cooking_protein: { position: [-1, 2, 0] as const, color: "#96ceb4" },
    seasoning: { position: [1, 2, 0] as const, color: "#feca57" }
  };

  // Native Web Speech API implementation
  const speak = useCallback((text: string) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.8;
      utterance.pitch = 1;
      utterance.volume = isMuted ? 0 : 1;
      
      utterance.onstart = () => setSpeaking(true);
      utterance.onend = () => setSpeaking(false);
      utterance.onerror = () => setSpeaking(false);
      
      window.speechSynthesis.speak(utterance);
    } else {
      console.warn('Speech synthesis not supported in this browser');
    }
  }, [isMuted]);

  const cancel = useCallback(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    }
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' }, 
        audio: false 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsRecording(true);
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
    }
  };

  const stopRecording = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  const playVoiceInstruction = (instruction: string) => {
    if (speaking) {
      cancel();
    } else {
      speak(instruction);
    }
  };

  const nextStep = () => {
    if (currentStep < cookingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const TechniqueVisualization: React.FC<{ technique: string }> = ({ technique }) => {
    const config = techniques3D[technique as keyof typeof techniques3D];
    if (!config) return null;

    return (
      <Box position={config.position} args={[1, 1, 1]}>
        <meshStandardMaterial color={config.color} />
        <Text
          position={[0, 1.5, 0]}
          fontSize={0.3}
          color="white"
          anchorX="center"
          anchorY="middle"
        >
          {technique.replace('_', ' ').toUpperCase()}
        </Text>
      </Box>
    );
  };

  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <FloatingElement delay={0}>
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center">
              <Camera className="w-10 h-10 text-white" />
            </div>
          </FloatingElement>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            AR Cooking <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Assistant
            </span>
          </h1>
          <p className="text-xl text-purple-200 max-w-3xl mx-auto">
            Experience immersive cooking with augmented reality guidance, voice-controlled navigation, 
            and real-time technique visualization for perfect results every time.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* AR Camera View */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <GlassCard className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">AR Kitchen View</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={isMuted ? () => setIsMuted(false) : () => setIsMuted(true)}
                    className="p-2 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors"
                  >
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                  <button className="p-2 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors">
                    <Maximize className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="relative bg-black rounded-xl overflow-hidden mb-4">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-64 object-cover"
                />
                
                {/* AR Overlay */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Step indicator overlay */}
                  <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm rounded-lg p-3">
                    <div className="text-white text-sm font-semibold">
                      Step {currentStep + 1} of {cookingSteps.length}
                    </div>
                    <div className="text-cyan-300 text-xs">
                      {cookingSteps[currentStep].duration}
                    </div>
                  </div>

                  {/* Virtual cooking guides */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-black/70 backdrop-blur-sm rounded-lg p-3">
                      <div className="text-white text-sm font-medium mb-1">
                        {cookingSteps[currentStep].title}
                      </div>
                      <div className="text-cyan-200 text-xs">
                        {cookingSteps[currentStep].description}
                      </div>
                    </div>
                  </div>

                  {/* AR technique indicators */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="w-16 h-16 border-2 border-cyan-400 rounded-full flex items-center justify-center animate-pulse">
                      <div className="w-8 h-8 bg-cyan-400 rounded-full opacity-50"></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center space-x-4">
                {!isRecording ? (
                  <motion.button
                    onClick={startRecording}
                    className="flex items-center space-x-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-cyan-600 hover:to-blue-600 transition-all duration-200"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Camera className="w-5 h-5" />
                    <span>Start AR Mode</span>
                  </motion.button>
                ) : (
                  <motion.button
                    onClick={stopRecording}
                    className="flex items-center space-x-2 bg-red-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-600 transition-all duration-200"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Pause className="w-5 h-5" />
                    <span>Stop AR Mode</span>
                  </motion.button>
                )}
              </div>
            </GlassCard>

            {/* 3D Technique Visualization */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-6"
            >
              <GlassCard className="p-6">
                <h3 className="text-lg font-bold text-white mb-4">3D Technique Guide</h3>
                <div className="h-64 rounded-xl overflow-hidden">
                  <Canvas camera={{ position: [0, 0, 8], fov: 75 }}>
                    <ambientLight intensity={0.5} />
                    <pointLight position={[10, 10, 10]} />
                    
                    <TechniqueVisualization technique={cookingSteps[currentStep].technique} />
                    
                    <OrbitControls enableZoom={false} />
                  </Canvas>
                </div>
              </GlassCard>
            </motion.div>
          </motion.div>

          {/* Step-by-Step Instructions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <GlassCard className="p-6">
              <h2 className="text-xl font-bold text-white mb-6">Cooking Instructions</h2>
              
              {/* Current Step */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">
                    {cookingSteps[currentStep].title}
                  </h3>
                  <span className="bg-cyan-500/20 text-cyan-300 px-3 py-1 rounded-full text-sm">
                    {cookingSteps[currentStep].duration}
                  </span>
                </div>
                
                <p className="text-purple-200 mb-4">
                  {cookingSteps[currentStep].description}
                </p>

                {/* Voice Control */}
                <div className="flex space-x-3 mb-6">
                  <motion.button
                    onClick={() => playVoiceInstruction(cookingSteps[currentStep].voiceInstruction)}
                    className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {speaking ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    <span>{speaking ? 'Stop' : 'Play'} Voice Guide</span>
                  </motion.button>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white font-medium">Progress</span>
                  <span className="text-cyan-300">
                    {Math.round(((currentStep + 1) / cookingSteps.length) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-3">
                  <motion.div
                    className="h-3 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${((currentStep + 1) / cookingSteps.length) * 100}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between">
                <button
                  onClick={prevStep}
                  disabled={currentStep === 0}
                  className="flex items-center space-x-2 bg-white/10 text-white px-4 py-2 rounded-lg font-medium hover:bg-white/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Previous</span>
                </button>
                
                <button
                  onClick={nextStep}
                  disabled={currentStep === cookingSteps.length - 1}
                  className="flex items-center space-x-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-2 rounded-lg font-medium hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>Next</span>
                  <Play className="w-4 h-4" />
                </button>
              </div>
            </GlassCard>

            {/* All Steps Overview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-6"
            >
              <GlassCard className="p-6">
                <h3 className="text-lg font-bold text-white mb-4">Recipe Overview</h3>
                <div className="space-y-3">
                  {cookingSteps.map((step, index) => (
                    <div
                      key={step.id}
                      className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 cursor-pointer ${
                        index === currentStep
                          ? 'bg-cyan-500/20 border border-cyan-500/30'
                          : index < currentStep
                          ? 'bg-green-500/10 border border-green-500/20'
                          : 'bg-white/5 border border-white/10'
                      }`}
                      onClick={() => setCurrentStep(index)}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                        index === currentStep
                          ? 'bg-cyan-500 text-white'
                          : index < currentStep
                          ? 'bg-green-500 text-white'
                          : 'bg-white/20 text-purple-300'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="text-white font-medium">{step.title}</div>
                        <div className="text-purple-300 text-sm">{step.duration}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ARCookingAssistant;